import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { CheckCircle, FileText, TrendingUp, Target, Bell } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function DigitalEmployeeAvatar({ isActive, onFlash }: { isActive: boolean; onFlash: boolean }) {
  const { t } = useLanguage();
  
  return (
    <motion.div
      animate={{ y: [0, -8, 0], scale: onFlash ? [1, 1.08, 1] : 1 }}
      transition={{ y: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }, scale: { duration: 0.15 } }}
      className="relative"
    >
      <motion.div
        animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.15, 1] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/50 to-cyan-500/50 blur-xl"
        style={{ width: 100, height: 100, top: -10, left: -10 }}
      />
      
      <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-blue-400/50 flex items-center justify-center overflow-hidden">
        <motion.div
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent"
        />
        <div className="relative z-10">
          <TrendingUp className="w-10 h-10 text-blue-400" />
        </div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-2 left-2 w-3 h-3 border-l border-t border-blue-400/50" />
          <div className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-blue-400/50" />
          <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent" />
        </div>
      </div>
      
      <motion.div
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap"
      >
        <span className="px-3 py-1 text-xs font-medium text-blue-400 bg-blue-400/10 rounded-full border border-blue-400/30">
          {t("ai.emp.status")}
        </span>
      </motion.div>
    </motion.div>
  );
}

function WorkModule({
  icon: Icon, title, isActive, isComplete, bubbleText, showBubble, statsText, statsValue,
}: {
  icon: React.ElementType; title: string; isActive: boolean; isComplete: boolean; bubbleText: string; showBubble: boolean; statsText?: string; statsValue?: number;
}) {
  const [showStats, setShowStats] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(0);

  useEffect(() => {
    if (isActive && statsText && statsValue) {
      setShowStats(true);
      const target = statsValue;
      const duration = 600;
      const steps = 20;
      const increment = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCurrentNumber(target);
          clearInterval(timer);
        } else {
          setCurrentNumber(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
    return undefined;
  }, [isActive, statsText, statsValue]);

  return (
    <motion.div
      animate={{ scale: isActive ? [1, 1.03, 1] : 1, borderColor: isActive ? "rgba(59, 130, 246, 0.8)" : "rgba(71, 85, 105, 0.3)" }}
      transition={{ duration: 0.5 }}
      className="relative p-3 rounded-xl bg-slate-800/50 border-2 transition-all duration-300 flex flex-col items-center text-center"
      style={{ borderColor: isActive ? "rgba(59, 130, 246, 0.8)" : "rgba(71, 85, 105, 0.3)" }}
    >
      {isActive && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 rounded-xl bg-blue-500/10 pointer-events-none" />}
      
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${isActive ? "bg-blue-500/20" : "bg-slate-700/50"}`}>
        <Icon className={`w-5 h-5 ${isActive ? "text-blue-400" : "text-slate-400"}`} />
      </div>
      <span className={`text-xs font-medium leading-tight ${isActive ? "text-blue-300" : "text-slate-300"}`}>{title}</span>
      {showStats && statsText && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-blue-400 mt-1">
          {currentNumber}
        </motion.div>
      )}
      
      <AnimatePresence>
        {isComplete && (
          <motion.div 
            initial={{ scale: 0, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            exit={{ scale: 0, opacity: 0 }} 
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="absolute -top-1 -right-1"
          >
            <CheckCircle className="w-4 h-4 text-emerald-400" />
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showBubble && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap z-20"
          >
            <div className="px-2 py-1 text-xs font-medium text-blue-300 bg-blue-500/20 rounded-lg border border-blue-400/30 backdrop-blur-xs">
              {bubbleText}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-blue-500/20 border-r border-b border-blue-400/30 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function DataFlowStream({ isActive, text }: { isActive: boolean; text: string }) {
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute left-1/2 top-0 -translate-x-1/2 w-1 h-12 overflow-hidden">
          <motion.div
            initial={{ y: 40 }}
            animate={{ y: -40 }}
            transition={{ duration: 0.6, ease: "linear" }}
            className="absolute w-full h-8 bg-gradient-to-t from-blue-500/80 via-blue-400/50 to-transparent rounded-full"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: [0, 1, 1, 0], y: [-10, -20, -30, -40] }}
            transition={{ duration: 0.8, times: [0, 0.2, 0.7, 1] }}
            className="absolute left-4 top-1/2 whitespace-nowrap"
          >
            <span className="text-xs text-blue-300 bg-slate-900/80 px-2 py-1 rounded backdrop-blur-xs">{text}</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function DigitalEmployeeCard() {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);
  const [flashAvatar, setFlashAvatar] = useState(false);

  useEffect(() => {
    const sequence = [
      { phase: 0, duration: 800 },
      { phase: 1, duration: 1200 },
      { phase: 2, duration: 500 },
      { phase: 3, duration: 1200 },
      { phase: 4, duration: 500 },
      { phase: 5, duration: 1200 },
      { phase: 6, duration: 500 },
      { phase: 7, duration: 1200 },
      { phase: 8, duration: 1000 },
    ];
    
    let currentIndex = 0;
    
    const runSequence = () => {
      const current = sequence[currentIndex]!;
      setPhase(current.phase);
      
      if (current.phase === 2 || current.phase === 4 || current.phase === 6 || current.phase === 8) {
        setFlashAvatar(true);
        setTimeout(() => setFlashAvatar(false), 150);
      }
      
      currentIndex = (currentIndex + 1) % sequence.length;
      setTimeout(runSequence, current.duration);
    };
    
    const timer = setTimeout(runSequence, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative h-full rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 overflow-hidden flex flex-col">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(100, 200, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(100, 200, 255, 0.1) 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
      </div>
      
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-blue-500/10 blur-3xl rounded-full" />
      
      <div className="relative z-10 p-5 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-blue-400 animate-pulse" />
          <h3 className="text-lg font-semibold text-slate-100">{t("rateWorker.title")}</h3>
        </div>
      </div>
      
      <div className="relative z-10 p-6 flex flex-col items-center flex-1">
        <div className="mb-8">
          <DigitalEmployeeAvatar isActive={phase > 0} onFlash={flashAvatar} />
        </div>
        
        <div className="w-full relative">
          <DataFlowStream isActive={phase === 8} text="SLA 99.5%" />
          
          <div className="grid grid-cols-4 gap-2">
            <WorkModule
              icon={FileText}
              title={t("rateWorker.h1")}
              isActive={phase === 1}
              isComplete={phase >= 2}
              bubbleText="Parsing contracts..."
              showBubble={phase === 1}
              statsText="Indexed:"
              statsValue={128}
            />
            
            <WorkModule
              icon={TrendingUp}
              title={t("rateWorker.h2")}
              isActive={phase === 3}
              isComplete={phase >= 4}
              bubbleText="Aggregating rates..."
              showBubble={phase === 3}
              statsText="Sources:"
              statsValue={56}
            />
            
            <WorkModule
              icon={Target}
              title={t("rateWorker.h3")}
              isActive={phase === 5}
              isComplete={phase >= 6}
              bubbleText="Generating strategy..."
              showBubble={phase === 5}
            />
            
            <WorkModule
              icon={Bell}
              title={t("rateWorker.h4")}
              isActive={phase === 7}
              isComplete={phase >= 8}
              bubbleText="Monitoring..."
              showBubble={phase === 7}
            />
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
    </div>
  );
}